<template>
    <DsTable :is-scrollable="true" :is-responsive="true">
        <DsTableRow>
            <DsTableCell type="th">{{ boardLabel }}</DsTableCell>
            <DsTableCell v-if="showSets" type="th">Tah</DsTableCell>
            <template v-if="lottery === Lottery.kameny">
                <DsTableCell type="th">Uhodnuté číslo</DsTableCell>
                <DsTableCell type="th">Uhodnuté kombinace čísel</DsTableCell>
            </template>
            <DsTableCell v-else type="th">Uhodnutá čísla</DsTableCell>
            <DsTableCell v-if="showMatchingNumbersCount" type="th">Počet</DsTableCell>
            <DsTableCell v-if="showTotalNumbersCount" type="th">Počet tipovaných</DsTableCell>
            <DsTableCell v-if="showWinOrder" type="th">Pořadí</DsTableCell>
            <DsTableCell v-if="showDeposit" type="th">Vklad</DsTableCell>
            <DsTableCell type="th">Výhra</DsTableCell>
            <DsTableCell v-if="showWinnersCount" type="th">Počet výherců</DsTableCell>
            <DsTableCell v-if="showExtraWin" type="th">Extra výhra</DsTableCell>
        </DsTableRow>
        <DsTableRow v-for="(boardResult, index) in results" :key="index">
            <DsTableCell>{{ getBoardLabel(boardResult.boardIndex, boardResult.setIndex) }}</DsTableCell>
            <DsTableCell v-if="showSets">{{ boardResult.setIndex + 1 }}. tah</DsTableCell>
            <template v-if="lottery === Lottery.kameny">
                <DsTableCell>{{ formatNumber(boardResult.matchingSumNumber) }}</DsTableCell>
                <DsTableCell
                    ><span v-html="formatMatchingCombinations(boardResult.matchingCombinations)"></span
                ></DsTableCell>
            </template>
            <DsTableCell v-else>{{ formatMatchingNumbers(boardResult) }}</DsTableCell>
            <DsTableCell v-if="showMatchingNumbersCount">{{ formatMatchingNumbersCount(boardResult) }}</DsTableCell>
            <DsTableCell v-if="showTotalNumbersCount">{{ getTotalNumbersCount(boardResult.boardIndex) }}</DsTableCell>
            <DsTableCell v-if="showWinOrder">{{ boardResult.winOrder }}</DsTableCell>
            <DsTableCell v-if="showDeposit"
                ><span v-html="formatNumberWithCurrency(getStake(boardResult.boardIndex))"></span
            ></DsTableCell>
            <DsTableCell><span v-html="formatNumberWithCurrency(boardResult.winAmount)"></span></DsTableCell>
            <DsTableCell v-if="showWinnersCount">{{ formatNumber(boardResult.winnersCount) }}</DsTableCell>
            <DsTableCell v-if="showExtraWin"
                ><span v-html="formatNumberWithCurrency(boardResult.extraWinAmount)"></span
            ></DsTableCell>
        </DsTableRow>
    </DsTable>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import DsTable from "@sazka/design-system/src/table/ds-table.vue";
import DsTableCell from "@sazka/design-system/src/table-cell/ds-table-cell.vue";
import DsTableRow from "@sazka/design-system/src/table-row/ds-table-row.vue";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { KamenyUtils } from "@sazka/lotteries/js/utils/kameny-utils";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { StringUtils } from "@sazka/utils/string-utils";
import { TicketCheckResultsBoard } from "@/lotteries/model/ticket-check/ticket-check";
import { usePpWagerConfiguration } from "../../composables/use-wager-configuration";
import { usePpWagerPriceCalculator } from "../../composables/use-wager-price-calculator";
import { useWagerBoardStore } from "../../stores/use-wager-board-store";

const props = defineProps<{
    lottery: Lottery;
    results: TicketCheckResultsBoard[];
}>();
const EMPTY_CELL_STRING = "–––";

const wagerStore = useWagerBoardStore();
const { kasickaSystemPrice } = usePpWagerPriceCalculator();
const { config } = usePpWagerConfiguration();

const boardLabel = computed(() => LotteryParser.boardLabel(props.lottery));
const showSets = computed(() => props.lottery === Lottery.sportka);
const showExtraWin = computed(() => props.lottery === Lottery.stastnych10);
const showMatchingNumbersCount = computed(() => {
    switch (props.lottery) {
        case Lottery.kameny:
        case Lottery.stastneDatum:
        case Lottery.rychla6:
            return false;
        default:
            return true;
    }
});
const showTotalNumbersCount = computed(() => {
    switch (props.lottery) {
        case Lottery.stastnych10:
        case Lottery.keno:
        case Lottery.kasicka:
            return true;
        default:
            return false;
    }
});
const showWinOrder = computed(() => {
    switch (props.lottery) {
        case Lottery.sportka:
        case Lottery.eurojackpot:
        case Lottery.euromiliony:
        case Lottery.extraRenta:
        case Lottery.miniRenta:
            return true;
        default:
            return false;
    }
});
const showWinnersCount = computed(() => {
    switch (props.lottery) {
        case Lottery.sportka:
        case Lottery.eurojackpot:
        case Lottery.euromiliony:
            return true;
        default:
            return false;
    }
});
const showDeposit = computed(() => {
    switch (props.lottery) {
        case Lottery.stastnych10:
        case Lottery.keno:
        case Lottery.kasicka:
        case Lottery.extraRenta:
        case Lottery.kameny:
            return true;
        default:
            return false;
    }
});

const getBoardLabel = (boardIndex: number, setIndex: number) => {
    if (props.lottery == Lottery.stastnych10 && setIndex == 1) {
        return "Královské číslo";
    }

    if (props.lottery === Lottery.kasicka) {
        const board = wagerStore.boards[boardIndex];
        return wagerStore.isSystem ? "Kombinovaná" : `Sázka na ${LotteryParser.getNumberWithLabel(board.primaryCount)}`;
    }

    return `${boardIndex + 1}. ${boardLabel.value}`;
};

const getStake = (boardIndex: number) => {
    let stake = wagerStore.boards[boardIndex]?.stake;

    if (props.lottery === Lottery.kasicka && wagerStore.isSystem) {
        stake = kasickaSystemPrice();
    }

    if (wagerStore.multiplier) {
        stake = stake * wagerStore.multiplier;
    }

    return stake;
};

const getTotalNumbersCount = (boardIndex: number) => {
    if (wagerStore.isSystem) {
        return wagerStore.systemBoard.primary.length;
    } else {
        return wagerStore.boards[boardIndex]?.primaryCount;
    }
};

const formatNumberWithCurrency = (amount: number) => {
    if (amount) {
        return StringUtils.formatCurrency(amount);
    }

    return EMPTY_CELL_STRING;
};

const formatNumber = (count: number) => {
    return count || EMPTY_CELL_STRING;
};

const formatMatchingNumbers = (board: TicketCheckResultsBoard) => {
    let formattedPrimary = board.matchingPrimary?.join(", ");

    if (config.secondaryBoardSize) {
        let formattedSecondary = board.matchingSecondary?.join(", ");
        formattedPrimary = formattedPrimary || "X";
        formattedSecondary = formattedSecondary || "X";

        return `${formattedPrimary} - ${formattedSecondary}`;
    }

    if (board.matchingExtraNumber) {
        return `${formattedPrimary} + ${board.matchingExtraNumber}`;
    }

    return formattedPrimary || EMPTY_CELL_STRING;
};

const formatMatchingNumbersCount = (board: TicketCheckResultsBoard) => {
    const primaryCount = board.matchingPrimary?.length;

    if (config.secondaryBoardSize) {
        const secondaryCount = board.matchingSecondary?.length;
        const formattedPrimaryCount = primaryCount || "0";
        const formattedSecondaryCount = secondaryCount || "0";

        return `${formattedPrimaryCount} - ${formattedSecondaryCount}`;
    }

    if (board.matchingExtraNumber) {
        return `${primaryCount} + 1`;
    }

    return primaryCount || EMPTY_CELL_STRING;
};

const formatMatchingCombinations = (combinations: number[]) => {
    if (!combinations?.length) {
        return EMPTY_CELL_STRING;
    }

    return KamenyUtils.mapKamenyNumbers(combinations)
        .figures.map((x) => x.text)
        .join(", ");
};
</script>
